import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';


const LoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [currentFeature, setCurrentFeature] = useState(0);

  // ✅ Added show password state
  const [showPassword, setShowPassword] = useState(false);

  const features = [
    { 
      title: "Secure Login", 
      description: "Two-step verification for enhanced security", 
      gradient: "from-green-500 to-emerald-600",
      icon: "🔒"
    },
    { 
      title: "Zero Platform Fees", 
      description: "100% of donations go to your cause", 
      gradient: "from-teal-500 to-teal-600",
      icon: "💰"
    },
    { 
      title: "Quick Access", 
      description: "Get back to managing your fundraiser instantly", 
      gradient: "from-purple-500 to-indigo-600",
      icon: "⚡"
    }
  ];

  // Initialize features rotation
  useEffect(() => {
    const featureInterval = setInterval(() => {
      setCurrentFeature(prev => (prev + 1) % features.length);
    }, 4000);

    return () => clearInterval(featureInterval);
  }, []);

  // Validation functions
  const validateField = (name, value) => {
    let error = '';
    
    switch (name) {
      case 'email':
        if (!value.trim()) error = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(value)) error = 'Please enter a valid email address';
        break;

      case 'password':
        if (!value.trim()) error = 'Password is required';
        break;

      default:
        break;
    }
    
    return error;
  };

  const handleBlur = (field) => {
    setTouchedFields(prev => ({ ...prev, [field]: true }));
    
    const error = validateField(field, formData[field]);
    setFieldErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({ ...prev, [name]: value }));

    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (apiError) {
      setApiError('');
    }
  };

  const handleLogin = async (e) => {
  e.preventDefault();

  const emailError = validateField('email', formData.email);
  const passwordError = validateField('password', formData.password);

  if (emailError || passwordError) {
    setFieldErrors({
      email: emailError,
      password: passwordError
    });
    setTouchedFields({
      email: true,
      password: true
    });
    return;
  }

  try {
    setIsLoading(true);
    setApiError('');

    const payload = {
      identifier: formData.email,
      password: formData.password
    };

    const response = await fetch("https://api.donatepe.org/auth/login", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    if (data.success) {
      // ✅ Store user data until browser is open
      sessionStorage.setItem("user", JSON.stringify(data.user));

      // ✅ Navigate to home
      navigate('/');
    } else {
      setApiError("Invalid email or password. Please try again.");
    }

  } catch (error) {
    setApiError("Login failed. Please try again.");
  } finally {
    setIsLoading(false);
  }
};


  const handleLoginWithPassword = () => {
    navigate('/start-fundraiser');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-teal-50">
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 flex items-center space-x-3">
            <div className="w-5 h-5 border-2 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-gray-700">
              Logging in...
            </span>
          </div>
        </div>
      )}
      
      <div className="relative min-h-screen flex">
        {/* Left Section */}
        <div className="hidden lg:flex lg:w-1/3 relative bg-gradient-to-br from-teal-50 via-white to-white">
          <div className="relative z-10 flex flex-col justify-center items-start w-full h-full p-12">
            <div className="max-w-lg">
              {/* Logo & Title */}
              <div className={`mb-12 transition-all duration-1000`}>
                <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl mb-8 shadow-2xl shadow-teal-500/20">
                  <span className="text-3xl text-white">🔐</span>
                </div>
                
                <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  Secure <span className="text-teal-600">Login</span>
                </h1>
                
                <p className="text-xl text-gray-600 leading-relaxed">
                  Access your fundraiser account with secure authentication.
                </p>
              </div>
              
              {/* Features */}
              <div className={`transition-all duration-1000 delay-300`}>
                <div className="space-y-5">
                  {features.map((feature, index) => (
                    <div 
                      key={index}
                      className={`p-5 bg-white/95 backdrop-blur-sm rounded-2xl border-2 transition-all duration-500 
                        ${currentFeature === index ? 'border-teal-300 shadow-xl shadow-teal-500/10 scale-[1.02]' : 
                          'border-gray-100 shadow-sm hover:border-gray-200'
                        }
                      `}
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center flex-shrink-0 shadow-md`}>
                          <span className="text-2xl">{feature.icon}</span>
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 text-lg mb-1.5">{feature.title}</h4>
                          <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Center Content */}
        <div className="w-full lg:w-2/3 flex flex-col justify-center items-center py-6 px-4 lg:px-8">
          <div className="w-full max-w-3xl mx-auto">
            {/* Centered Heading Section */}
            <div className="text-center mb-10 w-full">
              <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
                Login to Your Account
              </h2>
              <p className="text-gray-600 mt-3 text-lg">
                Enter your email and password to continue
              </p>
            </div>
            
            {/* Form Container */}
            <div className="w-full flex justify-center">
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 w-full max-w-2xl">
                {/* Header */}
                <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-teal-50 to-white">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm">
                      <span className="text-lg text-white">
                        🔐
                      </span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        Login Details
                      </h3>
                      <p className="text-xs text-gray-600 mt-0.5">
                        Enter your email and password
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Form Content */}
                <div className="px-6 py-5">
                  {/* Display API error */}
                  {apiError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-700">{apiError}</p>
                    </div>
                  )}

                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Email Field */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          onBlur={() => handleBlur('email')}
                          placeholder="Enter your email"
                          className={`
                            w-full px-3.5 py-3 rounded-lg border-2 transition-all duration-300
                            focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-500
                            placeholder:text-gray-400 text-gray-900 text-sm
                            ${fieldErrors.email && touchedFields.email 
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                              : 'border-gray-200 hover:border-gray-300'
                            }
                          `}
                        />
                        {fieldErrors.email && touchedFields.email && (
                          <p className="mt-1 text-xs text-red-600 text-left">
                            {fieldErrors.email}
                          </p>
                        )}
                      </div>

                      {/* Password Field */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                          Password <span className="text-red-500">*</span>
                        </label>

                        {/* ✅ Wrapped input inside relative div */}
                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            onBlur={() => handleBlur('password')}
                            placeholder="Enter your password"
                            className={`
                              w-full px-3.5 py-3 pr-12 rounded-lg border-2 transition-all duration-300
                              focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-500
                              placeholder:text-gray-400 text-gray-900 text-sm
                              ${fieldErrors.password && touchedFields.password 
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                                : 'border-gray-200 hover:border-gray-300'
                              }
                            `}
                          />

                          {/* ✅ Show/Hide Button */}
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-teal-600 transition"
                          >
                            {showPassword ? "🙈" : "👁️"}
                          </button>
                        </div>

                        {fieldErrors.password && touchedFields.password && (
                          <p className="mt-1 text-xs text-red-600 text-left">
                            {fieldErrors.password}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Submit Buttons */}
                    <div className="mt-6 pt-5 border-t border-gray-100">
                      <div className="space-y-3">
                        <button
                          type="submit"
                          disabled={isLoading}
                          className={`
                            w-full px-6 py-2.5 rounded-lg transition-all duration-300 font-semibold text-sm
                            ${isLoading
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-gradient-to-r from-teal-600 to-teal-700 text-white hover:shadow-md hover:shadow-teal-500/25'
                            }
                            flex items-center justify-center
                          `}
                        >
                          {isLoading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                              Logging in...
                            </>
                          ) : 'Login'}
                        </button>

                        <div className="relative flex items-center justify-center">
                          <div className="flex-grow border-t border-gray-200"></div>
                          <span className="flex-shrink-0 px-3 text-xs text-gray-500">OR</span>
                          <div className="flex-grow border-t border-gray-200"></div>
                        </div>

                        <button
                          type="button"
                          onClick={handleLoginWithPassword}
                          className="w-full px-6 py-2.5 rounded-lg border-2 border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 hover:border-gray-300 transition-all duration-300"
                        >
                          Create New Account
                        </button>
                      </div>
                    </div>
                  </form>

                  {/* Links */}
                  <div className="text-center pt-4 mt-4 border-t border-gray-100">
                    <div className="space-y-2">
                      <p className="text-xs text-gray-600">
                        Don't have an account?{' '}
                        <a
                          href="/start-fundraiser"
                          className="font-semibold text-teal-600 hover:text-teal-700 hover:underline transition-colors"
                        >
                          Start a fundraiser
                        </a>
                      </p>
                      <p className="text-xs text-gray-600">
                        Having trouble?{' '}
                        <a
                          href="/support"
                          className="font-semibold text-teal-600 hover:text-teal-700 hover:underline transition-colors"
                        >
                          Contact support
                        </a>
                      </p>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center mt-8 w-full">
              <p className="text-xs text-gray-500">
                By logging in, you agree to our Terms & Privacy Policy
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
